import xhr from 'xhr';

/**
 * Finanzas (admin › Finanzas: Resumen, Bancos y Flujo de caja).
 * Endpoints en klazia-api: src/api/finance. Por ahora solo supervisor_admin entra, registra, edita y
 * revisa (Revisado / Observado).
 */

export const READ_ROLES = ['supervisor_admin'];
export const EDIT_ROLES = ['supervisor_admin'];
export const REVIEW_ROLES = ['supervisor_admin'];

export const BRAND = '#574feb';
export const IN_COLOR = '#574feb';
export const OUT_COLOR = '#ed8936';

export const MONTHS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const MONTHS_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

/** Estado del mes de una cuenta (lo calcula klazia-api). `null` = no se muestra etiqueta. */
export const PERIOD_STATUS = {
  primer_mes: { label: 'Primer mes', bg: 'purple.100', color: 'purple.800' },
  cuadra: { label: 'Cuadra', bg: 'green.100', color: 'green.800' },
  diferencia: { label: 'Sin explicar', bg: 'orange.100', color: 'orange.800' },
  explicada: { label: 'Explicada', bg: 'blue.100', color: 'blue.800' },
  falta: { label: 'Falta caja inicial', bg: 'gray.100', color: 'gray.700' },
  sin_datos: { label: 'Sin datos', bg: 'gray.100', color: 'gray.600' },
  automatica: null,
};

export const REVIEW_LABEL = { revisado: 'Revisado', observado: 'Observado' };

export const errorMessage = (e) => e?.response?.data?.error?.message || 'No se pudo completar la acción.';

export const monthLabel = (year, month) => `${MONTHS[month - 1].charAt(0).toUpperCase()}${MONTHS[month - 1].slice(1)} ${year}`;

export function formatMoney(v, currency = 'COP') {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  const abs = Math.abs(n).toLocaleString('es-CO', { maximumFractionDigits: currency === 'USD' ? 2 : 0 });
  return `${n < 0 ? '−' : ''}${currency === 'USD' ? 'US$' : '$'}${abs}`;
}

/** Monto sin signo de moneda, para columnas de tabla. */
export const formatAmount = (v, currency = 'COP') =>
  v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('es-CO', { maximumFractionDigits: currency === 'USD' ? 2 : 0 });

/** En millones con una decimal, para gráficas: 50,9. */
export const formatMillions = (v) => (Number(v || 0) / 1e6).toLocaleString('es-CO', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function formatDate(d) {
  if (!d) return '—';
  const [y, m, day] = String(d).slice(0, 10).split('-');
  return `${day} ${MONTHS_SHORT[Number(m) - 1]?.toLowerCase()} ${y}`;
}

export const formatDay = (d) => (d ? `${String(d).slice(8, 10)} ${MONTHS_SHORT[Number(String(d).slice(5, 7)) - 1]?.toLowerCase()}` : '—');

export const todayIso = () => new Date().toLocaleString('en-CA', { timeZone: 'America/Bogota' }).slice(0, 10);

export function shiftMonth({ year, month }, delta) {
  const i = year * 12 + (month - 1) + delta;
  return { year: Math.floor(i / 12), month: (i % 12) + 1 };
}

function query(params) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '' && v !== false) qs.append(k, v);
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export async function getFinanceOptions() {
  const { data } = await xhr.get('/finance/options');
  return data;
}

export async function getOverview(params) {
  const { data } = await xhr.get(`/finance/overview${query(params)}`);
  return data;
}

export async function getSummary(params) {
  const { data } = await xhr.get(`/finance/summary${query(params)}`);
  return data;
}

export async function getPeriods(params) {
  const { data } = await xhr.get(`/finance/periods${query(params)}`);
  return data;
}

export async function savePeriod(body) {
  const { data } = await xhr.put('/finance/periods', body);
  return data;
}

export async function getMovements(params) {
  const { data } = await xhr.get(`/finance/movements${query(params)}`);
  return data;
}

export async function getMovementHistory(id) {
  const { data } = await xhr.get(`/finance/movements/${id}/history`);
  return data;
}

export async function createMovement(body) {
  const { data } = await xhr.post('/finance/movements', body);
  return data;
}

export async function updateMovement(id, body) {
  const { data } = await xhr.put(`/finance/movements/${id}`, body);
  return data;
}

export async function voidMovement(id, reason) {
  const { data } = await xhr.post(`/finance/movements/${id}/void`, { reason });
  return data;
}

export async function reviewMovement(id, status, comment) {
  const { data } = await xhr.post(`/finance/movements/${id}/review`, { status, comment });
  return data;
}

export async function uploadAttachments(id, files) {
  const form = new FormData();
  Array.from(files).forEach((f) => form.append('files', f));
  const { data } = await xhr.post(`/finance/movements/${id}/attachments`, form);
  return data;
}

export async function createTransfer(body) {
  const { data } = await xhr.post('/finance/transfers', body);
  return data;
}

export async function bulkCategory(body) {
  const { data } = await xhr.post('/finance/movements/bulk-category', body);
  return data;
}

export async function undoBulkCategory(auditId) {
  const { data } = await xhr.post('/finance/movements/bulk-category/undo', { auditId });
  return data;
}

export async function createCategory(body) {
  const { data } = await xhr.post('/finance/categories', body);
  return data;
}

export async function updateCategory(id, body) {
  const { data } = await xhr.put(`/finance/categories/${id}`, body);
  return data;
}

export async function updateAccountLimits(id, body) {
  const { data } = await xhr.put(`/finance/accounts/${id}/limits`, body);
  return data;
}

/** Descarga el Excel (esta cuenta o todas las del libro) del mes. */
export async function downloadExport(params) {
  const res = await xhr.get(`/finance/export${query(params)}`, { responseType: 'blob' });
  const disposition = res.headers?.['content-disposition'] || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const href = window.URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = match ? match[1] : 'finanzas.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(href);
}
